const store = new Map();

export function getCached(key) {
  const entry = store.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    store.delete(key);
    return null;
  }
  return entry.value;
}

export function setCached(key, value, ttlSeconds) {
  store.set(key, { value, expiresAt: Date.now() + ttlSeconds * 1000 });
}

// ── Statscore fetch cache ────────────────────────────────────────────────────
// Per-endpoint TTLs (seconds). Tune here.
export const TTL_LONG = 24 * 60 * 60; // 24h — profiles, teams, squads, name search, bracket, player seasons list
export const TTL_MED = 30 * 60;       // 30m — season participants-stats, event lists / fixtures, wc fixtures tab
export const TTL_SHORT = 10 * 60;     // 10m — standings, golden boot (move during live matches)
export const TTL_1H = 60 * 60;         // 1h - group tables, player hero, positional stats & trend ratings
export const TTL_2H = 2 * 60 * 60;     // 2h - golden boot, player top positional performers
export const TTL_3H = 3 * 60 * 60;     // 3h - knockout tree
export const TTL_7D = 7 * 24 * 60 * 60; // 7d - squad list, teams, photos
export const TTL_PERMANENT = 365 * 24 * 60 * 60; // 1y - frozen post-event data (e.g. World Cup 2026, tournament finished)

// Strip the rotating ?token=... so every token maps to one cache entry.
function cacheKeyForUrl(url) {
  return url.replace(/([?&])token=[^&]*/, '$1token=');
}

// L1 (in-memory) cached fetch for Statscore GET calls. Returns parsed JSON.
// - key: override the default token-stripped URL key (rarely needed).
// - keyPrefix: namespace the default key (still token-stripped) so a shared URL
//   gets its own cache entry per caller — e.g. the finished-World-Cup page cannot
//   collide with a live player page requesting the same participant-stats URL.
// - force: skip the cached read (e.g. editor ?refresh=true) but still refresh the entry.
// - Only successful responses (data.api.data present) are cached; errors are not.
// On an L1 hit no network request is made, so the Statscore subrequest disappears.
export async function cachedFetchJson(url, ttlSeconds = TTL_MED, { key, keyPrefix, force = false } = {}) {
  const cacheKey = key || (keyPrefix ? `${keyPrefix}:${cacheKeyForUrl(url)}` : cacheKeyForUrl(url));

  if (!force) {
    const hit = getCached(cacheKey);
    if (hit !== null) {
      return hit;
    }
  }

  const res = await fetch(url);
  let data;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (res.ok && data?.api?.data) {
    setCached(cacheKey, data, ttlSeconds);
  }
  return data;
}
